import { Component } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import {
  TimeZoneChangeEditor,
  TimeZoneChangeOption,
  TimeZoneFilters,
  TimeZoneSelectOptions
} from '../../../models/time-zones/time-zone.models';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { DateService } from '../../../services/date/date.service';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { DailyLogPage } from '../../daily-log/daily-log/daily-log';
import { NavMainMenuPage } from '../../nav/nav-menus/nav-main-menu/nav-main-menu';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { DailyLogPermissions } from '../../../models/permissions/permission.models';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { cloneDeep } from 'lodash';
import { DailyLogOperationInstance } from '../../../models/projects/operation.models';
import { DailyLogModalPage } from '../../daily-log/daily-log-modal/daily-log-modal';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';

@IonicPage()
@Component({
  selector: 'page-timezone-change',
  templateUrl: 'timezone-change.html',
})
export class TimezoneChangePage {
  public mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  public localTimeMask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];

  config = AppConfig.settings;

  isWeb: boolean;
  loading: boolean;

  dailyLogDate: PtLuxonDate;
  endOfDay: PtLuxonDate;
  currentTimeChanged: string;
  timeZoneChangeEditor: TimeZoneChangeEditor = new TimeZoneChangeEditor();
  permissions: DailyLogPermissions = new DailyLogPermissions();

  latestTimeZoneChange: TimeZoneChangeEditor;
  currentTimeZone: TimeZoneChangeEditor;
  currentTimeZoneText: string;
  timeZoneSelectOptions: any = new TimeZoneSelectOptions().options;
  timeZoneOptions: TimeZoneChangeOption<number>[] = this.initialiseTimeZoneOptions();
  timeZoneSelectId: number;
  noCurrentTimeZone: boolean;
  newTimeAfterChange: string;
  newTimeOffset: string;
  showTimeZoneChronologicalWarning: boolean;
  showTimeZoneAlreadyLoggedWarning: boolean;
  showImplementerModeWarning: boolean;
  latestChangeDateText: string;
  latestChangeAfterDateText: string;
  showDateLineWarning: boolean;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public platform: Platform,
    private dateService: DateService,
    private dailyLogService: DailyLogService,
    private operationService: OperationService,
    private appDataService: AppDataSyncService,
    private timeZoneService: TimeZoneService,
    private toastCtrl: ToastController,
    private navHelperService: NavHelperService,
    private permissionsService: PermissionsService,
    private appDataSyncService: AppDataSyncService,
    private modalController: ModalController,
  ) {
    this.checkIsImplementerMode();
  }

  ionViewWillLoad() {
    this.loading = true;
  }

  async ionViewDidLoad() {
    const date = new PtLuxonDate();
    this.isWeb = this.platform.is('core');
    this.permissions = await this.permissionsService.dailyLogPermissions();

    this.currentTimeChanged = this.dateService.setTimeStringFromPtLuxonDate(date);

    const timeZoneFilters = new TimeZoneFilters();
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    this.endOfDay = await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime);
    this.timeZoneChangeEditor.dateTime = cloneDeep(this.dailyLogDate.dateTime);
    timeZoneFilters.dateTime = (await this.dateService.setPtLuxonDateFromUserEnteredString(this.currentTimeChanged, this.dailyLogDate.dateTime)).dateTime;

    // creating a new timezone change may have caused another timezone change to be deleted, so the appdata needs to be updated
    // TODO: Create a separate call to just refresh timezones without calling the GetNonDailyLogData endpoint - would be much faster
    await this.appDataSyncService.updateAppDataForDate(this.dailyLogDate, true);

    this.latestTimeZoneChange = await this.timeZoneService.getLatestTimeZoneChange();
    const timeZone = await this.timeZoneService.getEditor(timeZoneFilters);

    if (!!timeZone) {
      this.currentTimeZone = cloneDeep(timeZone);
      const timeHours = timeZone.secondsOffsetFromUtc / 60 / 60;
      this.currentTimeZoneText = timeHours > 0 ? '+ ' + timeHours : timeHours.toString().replace('-', '- ');
    } else {
      this.timeZoneChangeEditor.dateTime = this.dailyLogDate.dateTime.minus({day: 1});
    }

    if (timeZoneFilters.dateTime < this.latestTimeZoneChange.dateTime) {
      this.showTimeZoneChronologicalWarning = true;
      this.latestChangeDateText = await this.dateService
        .setFullTimeString(this.latestTimeZoneChange.dateTime);
      this.latestChangeAfterDateText = await this.dateService
        .setFullTimeString(this.latestTimeZoneChange.dateTime.plus({seconds: this.latestTimeZoneChange.secondsOffsetFromUtc}));
    } else {
      this.showTimeZoneChronologicalWarning = false;
    }

    this.loading = false;
  }

  async timeOfChangeAltered() {
    const valid = this.dateService.timeIsValid(this.currentTimeChanged);

    if (!valid || this.currentTimeChanged === '') {
      return;
    }

    this.loading = true;

    const ptDate = await this.dateService.setPtLuxonDateFromUserEnteredString(
      this.currentTimeChanged, this.dailyLogDate.dateTime);

    // add relevant offset and remove local system offset from latestTimeZoneDate
    let latestTimeZoneDate = this.latestTimeZoneChange.dateTime.plus({
      millisecond: (this.latestTimeZoneChange.secondsOffsetFromUtc * 1000) + (this.latestTimeZoneChange.dateTime.offset * 3600)
    })
    await this.updateWarningFlags(latestTimeZoneDate, ptDate);

    const timeZone = await this.timeZoneService.getEditorLocally({dateTime: ptDate.dateTime, dateAmbiguous: true});

    if (timeZone) {
      this.currentTimeZone = timeZone;
      const timeHours = timeZone.secondsOffsetFromUtc / 60 / 60;
      this.currentTimeZoneText = timeHours > 0 ? '+ ' + timeHours : timeHours.toString().replace('-', '- ');
      ;
    } else {
      this.noCurrentTimeZone = true;
    }

    this.calculateNewTimeAfterChange();

    this.loading = false;
  }

  offsetSelected() {
    this.calculateNewTimeAfterChange();
  }

  async calculateNewTimeAfterChange() {
    const selectedTimeZone = this.timeZoneOptions.find(e => e.id === this.timeZoneSelectId);

    if (selectedTimeZone) {
      this.newTimeOffset = selectedTimeZone.name;
      const newDate = await this.dateService.setPtLuxonDateFromUserEnteredString(
        this.currentTimeChanged, this.dailyLogDate.dateTime);

      newDate.setOffset(selectedTimeZone.seconds, false);

      // if new date is on next day or on previous day, prevent save
      if (newDate.dateTime.ordinal !== this.dailyLogDate.dateTime.ordinal) {
        this.showDateLineWarning = true;
      } else {
        this.showDateLineWarning = false;
      }

      this.newTimeAfterChange = this.dateService.setTimeStringFromPtLuxonDate(newDate);
    }
  }

  async save() {
    this.loading = true;

    if (!!this.timeZoneChangeEditor.dateTime && this.currentTimeChanged != null && this.currentTimeChanged.length === 5) {
      this.timeZoneChangeEditor.timeOfChange = (await this.dateService.setPtLuxonDateFromUserEnteredString(
        this.currentTimeChanged, this.dailyLogDate.dateTime)).dateTime;
      this.timeZoneChangeEditor.timeOfChangeToShow = this.currentTimeChanged;

      this.timeZoneChangeEditor.dateTime = cloneDeep(this.timeZoneChangeEditor.timeOfChange);
      this.timeZoneChangeEditor.dateToShow = this.newTimeAfterChange;

      this.timeZoneChangeEditor.secondsOffsetFromUtc = this.timeZoneOptions.find(e => e.id === this.timeZoneSelectId).seconds;
      this.timeZoneChangeEditor.previousSecondsOffsetFromUtc = this.currentTimeZone.secondsOffsetFromUtc;

      await this.timeZoneService.saveEditor(this.timeZoneChangeEditor);
    }

    this.presentSaveToast().then(() => {
      this.navCtrl.setRoot(DailyLogPage);
      this.navHelperService.updateNavMenuPage({page: NavMainMenuPage});
    });

    this.loading = false;
  }

  regexChange(inputTime: string) {
    if (inputTime.charAt(0) === '2') {
      this.mask[1] = new RegExp('[0-3]');
    } else {
      this.mask[1] = new RegExp('\\d');
    }
  }

  localRegexChange(inputTime: string) {
    if (inputTime.charAt(0) === '2') {
      this.localTimeMask[1] = new RegExp('[0-3]');
    } else {
      this.localTimeMask[1] = new RegExp('\\d');
    }
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message: 'Time Zone Change Saved',
      duration: 3000,
      position: 'bottom'
    });

    return toast.present();
  }

  initialiseTimeZoneOptions(): TimeZoneChangeOption<number>[] {
    return [
      {id: 1, name: '- 12', seconds: -3600 * 12},
      {id: 2, name: '- 11', seconds: -3600 * 11},
      {id: 3, name: '- 10', seconds: -3600 * 10},
      {id: 4, name: '- 9', seconds: -3600 * 9},
      {id: 5, name: '- 8', seconds: -3600 * 8},
      {id: 6, name: '- 7', seconds: -3600 * 7},
      {id: 7, name: '- 6', seconds: -3600 * 6},
      {id: 8, name: '- 5', seconds: -3600 * 5},
      {id: 9, name: '- 4', seconds: -3600 * 4},
      {id: 10, name: '- 3', seconds: -3600 * 3},
      {id: 11, name: '- 2', seconds: -3600 * 2},
      {id: 12, name: '- 1', seconds: -3600},
      {id: 13, name: '0', seconds: 0},
      {id: 14, name: '+ 1', seconds: 3600},
      {id: 15, name: '+ 2', seconds: 3600 * 2},
      {id: 16, name: '+ 3', seconds: 3600 * 3},
      {id: 17, name: '+ 4', seconds: 3600 * 4},
      {id: 18, name: '+ 5', seconds: 3600 * 5},
      {id: 19, name: '+ 6', seconds: 3600 * 6},
      {id: 20, name: '+ 7', seconds: 3600 * 7},
      {id: 21, name: '+ 8', seconds: 3600 * 8},
      {id: 22, name: '+ 9', seconds: 3600 * 9},
      {id: 23, name: '+ 10', seconds: 3600 * 10},
      {id: 24, name: '+ 11', seconds: 3600 * 11},
      {id: 25, name: '+ 12', seconds: 3600 * 12},
      {id: 26, name: '+ 13', seconds: 3600 * 13},
      {id: 27, name: '+ 14', seconds: 3600 * 14}
    ];
  }

  async updateWarningFlags(latestTimeZoneDate: DateTime, dateEntered: PtLuxonDate) {
    if (dateEntered.dateTime < latestTimeZoneDate) {
      this.showTimeZoneChronologicalWarning = true;
      this.latestChangeDateText = await this.dateService.setFullTimeString(this.latestTimeZoneChange.dateTime);
      this.latestChangeAfterDateText = await this.dateService.setFullTimeString(this.latestTimeZoneChange.dateTime.plus({
        second: this.latestTimeZoneChange.secondsOffsetFromUtc
      }));
    } else {
      this.showTimeZoneChronologicalWarning = false;
    }

    if (dateEntered.dateTime.toMillis() === latestTimeZoneDate.toMillis()) {
      this.showTimeZoneAlreadyLoggedWarning = true;
    } else {
      this.showTimeZoneAlreadyLoggedWarning = false;
    }
  }

  async getAffectedOperations(timeZoneChangeDate: DateTime) {
    const operationInstances = await this.operationService.getAllProjectSpecificOperationInstances();

    return operationInstances.filter(p => p.startTime.dateTime >= timeZoneChangeDate || p.endTime.dateTime >= timeZoneChangeDate);
  }

  public openModal() {
    const dailyLogModal = this.modalController.create(DailyLogModalPage);
    dailyLogModal.present();
  }

  private checkIsImplementerMode() {
    this.showImplementerModeWarning = this.config.isImplementerEnvironment;
  }
}



// WEBPACK FOOTER //
// ./src/pages/setup/timezone-change/timezone-change.ts